import {
    getCurrentTempo,
    nextNote,
    getMeasuresForBlock,
    setCurrentNoteIndex,
    getCurrentNote,
    getCurrentNoteIndex,
    getBlockMinGameLevel
} from './notes.js';
import {highlightMelodyBlock, playSequence, setActiveMelodyBlock, stopSequence, updateStarsDisplay, updateFullMelodySequenceBlocks } from './guitar.js';
import {
    resetCurrentNoteId,
    initTuner,
    setListening,
    resumeListening,
    resetNoteDetection
} from "./tuner.js";
import {playBlock, stopBlockPlayback} from "./blockPlayer.js";
import {threeJSBand} from "./threejsBand";
import { notePlayer } from "./notePlayer";
import { startCountdown } from "./utils/countdown.js";
import { saveUserGameState, saveUserProgress, resetUserProgress } from "./api.js";
import { getUserConfig, getUserGameState as getCachedGameState, resetCachedUserProgress } from "./userconfig.js";
import auth from "./auth.js";
import { enterFullscreen, exitFullscreen } from "./utils/fullscreen.js";
import * as Tone from 'tone';
import { guitarEffects } from "./guitarEffects.js";
import { audioNormalizer } from "./audioNormalizer.js";
import { errorLogger } from "./errorLogger.js";
import { translate } from "./translation.js";

// Константы для Game
const GAME_CONFIG = {
    SPEED_STEPS: [0, 0.25, 0.5, 0.75, 1],
    SAVE_KEY: 'guitar_tuner_progress',
    DEFAULT_VALUES: {
        BLOCK_ID: 0,
        MEASURE_INDEX: 0,
        SPEED_STEP: 0,
        LEVEL: 0,
        ACCURACY: 0,
        CORRECT_COUNT: 0
    },
    ACCURACY_THRESHOLD: 90,
    MAX_ACCURACY_PERCENT: 100
};

class GameState {
    constructor() {
        this.reset();
    }

    reset() {
        this.isPlaying = false;
        this.currentMeasureIndex = 0;
        this.currentSpeedStep = 0;
        this.measures = [];
        this.accuracy = 0;
        this.correctCount = 0;
        this.currentLevel = 0;
        this.currentBlockId = null;
        this.stars = {};
        this.isGaming = false; // Сбрасываем флаг
        this.blockMeasures = [];
    }
}

class GameManager {
    constructor() {
        this.state = new GameState();
        this.speedSteps = GAME_CONFIG.SPEED_STEPS;
        this.backgroundPlayer = null;
        // Canvas для кардиографа
        this.heartbeatAnimation = null;
        this.heartbeatCanvas = null;
        this.heartbeatCtx = null;
        this.heartbeatDotPosition = 0;
        this.heartbeatWaveHistory = []; // История волны для отрисовки следа
        this.heartbeatJumpStartTime = null; // Время начала "удара сердца"
        this.heartbeatJumpAmplitude = 0; // Амплитуда текущего удара
        this.blockProgress = {};
        this.userId = null;
        this.songId = null;
        this.sessionStartTime = null;
        this.requestedBlockId = null; // ID блока, который был запрошен для запуска
        this.currentStartBlockPromise = null; // Промис текущего запуска блока
        this.startBlockAbortController = null; // AbortController для прерывания текущего запуска блока
    }

    stopBackgroundTrack() {
        if (this.backgroundPlayer) {
            try {
                this.backgroundPlayer.stop();
                this.backgroundPlayer.dispose();
            } catch (e) {
                console.warn('Не удалось остановить бэкграунд-трек блока:', e);
            }
            this.backgroundPlayer = null;
        }
    }

    async playBackgroundTrack(blockId) {
        this.stopBackgroundTrack();

        // Получаем текущий уровень сложности
        const speedStep = this.state.currentSpeedStep;

        // В нулевом режиме (speedStep === 0) фон не играем
        if (speedStep === 0) {
            return;
        }

        // speedStep уже соответствует номеру уровня в файле (1-4)
        const level = speedStep;

        try {
            // Убеждаемся, что Tone.js контекст активен
            const toneContext = Tone.getContext();
            if (toneContext.state !== 'running') {
                await Tone.start();
            }

            // Получаем songId (используем текущий или дефолтный)
            const songId = this.songId || 1;

            // Формируем URL по новой структуре: /audio/blocks/{songId}/${blockId}/speed_${level}.mp3
            const url = `/audio/blocks/${songId}/${blockId}/speed_${level}.mp3`;
            
            // Создаем новый Player
            this.backgroundPlayer = new Tone.Player({
                url,
                loop: false,
                autostart: true,
                volume: -12
                // playbackRate не устанавливаем - играем с оригинальной скоростью файла
            });

            // Нормализуем фоновую музыку
            audioNormalizer.initialize();
            audioNormalizer.normalizeBackgroundMusic(this.backgroundPlayer);
            
            // Получаем входной узел нормализатора и проверяем, что он валиден
            const normalizerInput = audioNormalizer.getInput();
            
            // Проверяем, что normalizerInput является валидным Tone.js AudioNode
            // Проверяем наличие context (указывает на AudioContext) и метода connect
            if (normalizerInput && 
                normalizerInput.context && 
                typeof normalizerInput.connect === 'function') {
                try {
                    // Подключаем Player к нормализатору (который уже подключен к destination)
                    this.backgroundPlayer.connect(normalizerInput);
                } catch (connectError) {
                    // Если подключение не удалось, используем fallback
                    console.warn('Failed to connect to audioNormalizer, using direct connection:', connectError);
                    this.backgroundPlayer.toDestination();
                }
            } else {
                // Fallback: подключаем напрямую к destination, если нормализатор не работает
                console.warn('AudioNormalizer input not available or invalid, connecting directly to destination', {
                    hasInput: !!normalizerInput,
                    hasContext: !!(normalizerInput && normalizerInput.context),
                    hasConnect: !!(normalizerInput && typeof normalizerInput.connect === 'function')
                });
                this.backgroundPlayer.toDestination();
            }
        } catch (e) {
            console.warn(`Не удалось запустить фон для блока ${blockId} уровня ${level}:`, e);
            this.backgroundPlayer = null;
        }
    }

    getGamingMode() {
        return this.state.isGaming;
    }

    // Сохранение прогресса (сервер + локально)
    async saveProgress({ completed = false, playTimeSeconds = 0, updateBestSpeedLevel = false } = {}) {
        const blockId = Number(this.state.currentBlockId);
        if (Number.isNaN(blockId)) {
            return;
        }

        console.log('💾 saveProgress called:', { 
            blockId, 
            completed, 
            playTimeSeconds, 
            updateBestSpeedLevel,
            isGuest: auth.isGuestUser(),
            userId: this.userId 
        });

        const existingProgress = this.blockProgress[blockId] || {};
        // best_speed_level обновляется ТОЛЬКО когда уровень успешно пройден (updateBestSpeedLevel = true)
        // Это предотвращает обновление best_speed_level когда пользователь просто дошел до уровня, но не прошел его
        const bestSpeedLevel = updateBestSpeedLevel 
            ? Math.max(Number(existingProgress.best_speed_level || 0), Number(this.state.currentSpeedStep || 0))
            : Number(existingProgress.best_speed_level || 0);
        // Звезды показывают количество пройденных уровней: best_speed_level + 1
        // best_speed_level = 0 → 1 звезда, best_speed_level = 4 → 5 звезд
        const stars = bestSpeedLevel + 1;
        const attempts = Number(existingProgress.attempts || 0) + 1;
        const userId = Number(this.userId || existingProgress.user_id || 1);
        const songId = Number(this.songId || existingProgress.song_id || 1);

        // Передаем только новое время игры (накопление происходит в БД)
        const newPlayTime = Number.isFinite(playTimeSeconds) && playTimeSeconds > 0 ? playTimeSeconds : 0;

        const progressPayload = {
            user_id: userId,
            song_id: songId,
            block_id: blockId,
            stars,
            accuracy: Number(this.state.accuracy || 0),
            best_speed_level: bestSpeedLevel,
            attempts,
            completed: completed ? 1 : Number(existingProgress.completed || 0),
            play_time_seconds: newPlayTime
        };

        try {
            // Если пользователь гость и прошел хотя бы один уровень сложности, регистрируем его на сервере
            // updateBestSpeedLevel = true означает что уровень успешно пройден
            if (updateBestSpeedLevel && auth.isGuestUser()) {
                console.log('Guest user completed a difficulty level, converting to permanent user');
                const convertResult = await auth.convertGuestToPermanent(progressPayload);
                if (convertResult.success) {
                    console.log('Guest successfully converted to permanent user, new ID:', convertResult.user.id);
                    // Обновляем userId для сохранения прогресса
                    progressPayload.user_id = convertResult.user.id;
                    this.userId = convertResult.user.id;
                } else {
                    console.error('Failed to convert guest:', convertResult.error);
                }
            }
            
            // Сохраняем прогресс только если есть user_id (не гость или уже зарегистрированный)
            if (progressPayload.user_id) {
                await saveUserProgress(progressPayload);
            } else if (auth.isGuestUser()) {
                console.log('Guest user progress saved only locally (not on server)');
            }
            
            // Обновляем локальное состояние с учетом накопленного времени в БД
            const updatedProgress = { ...existingProgress, ...progressPayload };
            // Время накапливается в БД, поэтому обновляем локальное состояние
            if (newPlayTime > 0) {
                updatedProgress.play_time_seconds = (existingProgress.play_time_seconds || 0) + newPlayTime;
            }
            this.blockProgress[blockId] = updatedProgress;
            
            // Обновляем визуальное состояние блоков в полной последовательности
            updateFullMelodySequenceBlocks();
        } catch (error) {
            console.error('Не удалось сохранить прогресс пользователя:', error);
            // Отправляем ошибку на сервер
            errorLogger.logError({
                error_type: 'save_progress_error',
                message: `Не удалось сохранить прогресс пользователя: ${error.message}`,
                stack: error.stack,
                url: window.location.href,
                additional_info: {
                    block_id: blockId,
                    user_id: userId,
                    song_id: songId,
                    progress_payload: progressPayload,
                    error_name: error.name
                }
            });
        }

        const stateData = {
            currentBlockId: this.state.currentBlockId,
            currentMeasureIndex: this.state.currentMeasureIndex,
            currentSpeedStep: this.state.currentSpeedStep,
            currentLevel: this.state.currentLevel,
            accuracy: this.state.accuracy,
            correctCount: this.state.correctCount,
            stars: this.state.stars,
            isGaming: this.state.isGaming,
            blockProgress: this.blockProgress,
            userId: this.userId, // Сохраняем userId для проверки при загрузке
        };

        // Локальное резервное сохранение
        localStorage.setItem(GAME_CONFIG.SAVE_KEY, JSON.stringify(stateData));

        // Серверное сохранение состояния игры
        try {
            await saveUserGameState({
                user_id: userId,
                state_data: stateData
            });
        } catch (error) {
            console.error('Не удалось сохранить состояние игры пользователя:', error);
            // Отправляем ошибку на сервер
            errorLogger.logError({
                error_type: 'save_game_state_error',
                message: `Не удалось сохранить состояние игры пользователя: ${error.message}`,
                stack: error.stack,
                url: window.location.href,
                additional_info: {
                    user_id: userId,
                    block_id: blockId,
                    error_name: error.name,
                    state_data_keys: Object.keys(stateData)
                }
            });
        }
    }

    async loadProgress() {
        try {
            const userConfig = await getUserConfig();
            
            // ВАЖНО: Используем userId из авторизации, а не дефолтный userId = 1
            // Дефолтный userId = 1 может быть пользователем с открытыми уровнями
            // Если пользователь авторизован, используем его ID, иначе null (не загружаем прогресс)
            if (auth.isLoggedIn()) {
                this.userId = auth.getUserId();
            } else {
                // Если пользователь не авторизован, используем userId из userConfig
                // но только если он определен (не дефолтный 1)
                this.userId = userConfig.user?.id || null;
                // Если userId = 1 и пользователь не авторизован, это может быть ошибка
                if (this.userId === 1) {
                    console.warn('Using default userId = 1 without authentication. This may load wrong user progress.');
                }
            }
            
            this.songId = userConfig.userProgress?.[0]?.song_id ?? null;

            this.blockProgress = {};
            
            // Загружаем прогресс только если userId определен
            if (this.userId && userConfig.userProgress) {
                (userConfig.userProgress || []).forEach(progress => {
                    this.blockProgress[progress.block_id] = progress;
                    // Звезды должны соответствовать best_speed_level + 1 (количество пройденных уровней)
                    const bestSpeedLevel = Number(progress.best_speed_level || 0);
                    // Звезды показывают количество пройденных уровней: best_speed_level + 1
                    // best_speed_level = 0 → 1 звезда, best_speed_level = 4 → 5 звезд
                    this.state.stars[progress.block_id] = bestSpeedLevel + 1;
                });
            } else {
                // Если userId не определен или прогресс пустой, очищаем блок прогресса
                this.blockProgress = {};
                this.state.stars = {};
            }

            let stateData = getCachedGameState()?.state_data;
            const savedLocal = localStorage.getItem(GAME_CONFIG.SAVE_KEY);

            if (typeof stateData === 'string') {
                try {
                    stateData = JSON.parse(stateData);
                } catch (e) {
                    stateData = null;
                }
            }

            if (!stateData && savedLocal) {
                try {
                    stateData = JSON.parse(savedLocal);
                } catch (e) {
                    stateData = null;
                }
            }

            // ВАЖНО: Загружаем stateData только если userId совпадает или userId не сохранен в stateData
            // Это предотвращает загрузку прогресса другого пользователя
            if (stateData) {
                // Если в stateData есть userId и он не совпадает с текущим, не загружаем
                if (stateData.userId && stateData.userId !== this.userId) {
                    console.warn('StateData userId mismatch, clearing cached state');
                    stateData = null;
                    // Очищаем localStorage, так как он содержит данные другого пользователя
                    localStorage.removeItem(GAME_CONFIG.SAVE_KEY);
                }
            }

            if (stateData) {
                const defaults = GAME_CONFIG.DEFAULT_VALUES;
                this.state.currentBlockId = stateData.currentBlockId ?? defaults.BLOCK_ID;
                this.state.currentMeasureIndex = stateData.currentMeasureIndex ?? defaults.MEASURE_INDEX;
                this.state.currentSpeedStep = stateData.currentSpeedStep ?? defaults.SPEED_STEP;
                this.state.currentLevel = stateData.currentLevel ?? defaults.LEVEL;
                this.state.accuracy = stateData.accuracy ?? defaults.ACCURACY;
                this.state.correctCount = stateData.correctCount ?? defaults.CORRECT_COUNT;
                // Данные из БД (this.state.stars) имеют приоритет над кешем
                // Сначала кеш, потом БД (БД перезапишет)
                if (stateData.stars) {
                    this.state.stars = { ...stateData.stars, ...this.state.stars };
                }
                this.state.isGaming = stateData.isGaming || false;

                if (stateData.blockProgress) {
                    // Данные из БД (this.blockProgress) имеют приоритет над кешем
                    // Сначала кеш, потом БД (БД перезапишет)
                    this.blockProgress = { ...stateData.blockProgress, ...this.blockProgress };
                }

                if (stateData.measures && stateData.measures.length > 0) {
                    // Включаем паузы в загруженную последовательность
                    this.state.measures = stateData.measures || [];
                }
            }
            
            // Обновляем визуальное состояние блоков в полной последовательности после загрузки прогресса
            updateFullMelodySequenceBlocks();
        } catch (error) {
            console.error('Ошибка загрузки прогресса пользователя:', error);
        }
    }

    // Инициализация игры
    initGame(track) {
        let gameTracks = [];
        track.forEach((blocks) => {
            // Фильтруем паузы из последовательности для игры
            gameTracks.push(blocks.flat().filter(chordGroup => {
                return !(chordGroup && chordGroup[0] && chordGroup[0].pause === "true");
            }));
        });

        this.state.measures = gameTracks.flat(); // Flatten again after filtering
        this.state.currentMeasureIndex = 0;
        this.state.currentSpeedStep = 0;
        this.state.currentLevel = 0;
        this.state.correctCount = 0;
        this.state.totalCount = 0;
        this.state.accuracy = 0;
        this.state.currentBlockId = 0;
        this.state.isGaming = true;
        this.state.blockMeasures = [];

        setCurrentNoteIndex(-1);
        resetCurrentNoteId();

        this.state.isPlaying = true;
        this.updateGameUI(); // Показываем пульс
        stopSequence();

        if (this.state.currentBlockId !== null) {
            setActiveMelodyBlock(this.state.currentBlockId);
        }
    }

    // Проверка завершения уровня
    async checkLevelCompletion() {
        // 1) Полностью останавливаем последовательность и сбрасываем состояние
        await this.stopGameSequence();
        let blockCompleted = false;
        
        // Сохраняем accuracy перед любыми изменениями для сохранения в БД
        let accuracyToSave = this.state.accuracy;

        if (threeJSBand) {
            threeJSBand.clearAllMovingNotes();
        }

        const displayAccuracy = Math.min(
            GAME_CONFIG.MAX_ACCURACY_PERCENT,
            this.state.accuracy
        );

        const passed = displayAccuracy >= GAME_CONFIG.ACCURACY_THRESHOLD;
        const isZeroLevel = this.state.currentSpeedStep === 0;
        const isNonZeroLevel = !isZeroLevel;
        const failedNonZeroLevel = !passed && isNonZeroLevel;

        // Если точность 0%, просто останавливаем игру после сообщения
        // НЕ сохраняем прогресс при точности 0%, чтобы не искажать статистику
        if (displayAccuracy === 0) {
            const result = await this.showResultMessage(displayAccuracy, {});
            // Не сохраняем прогресс при точности 0%
            resetCurrentNoteId();
            if (this.state.currentBlockId !== null) {
                highlightMelodyBlock(this.state.currentBlockId);
            }
            return;
        }

        // 2) Показываем сообщение с результатами.
        // Для нулевого уровня при успешном прохождении на этом же окне спрашиваем,
        // готов ли пользователь перейти на первый уровень.
        // Для неудачного прохождения уровня > 0 показываем кнопку отката.
        const result = await this.showResultMessage(displayAccuracy, {
            askForNextLevel: passed && isZeroLevel,
            askForStepBack: failedNonZeroLevel
        });
        const proceedFromZero = passed && isZeroLevel && result && result.proceedToNextLevel === true;
        const shouldStepBack = failedNonZeroLevel && result && result.stepBack === true;

        if (passed) {
            setCurrentNoteIndex(-1);

            this.state.accuracy = 0;
            this.state.correctCount = 0;

            // Для нулевого уровня: если пользователь выбрал "остаться",
            // НЕ сохраняем прогресс и сразу перезапускаем тот же блок на нулевом уровне.
            if (isZeroLevel && !proceedFromZero) {
                this.updateGameUI();

                // Запускаем всё как при повторе уровня (логика аналогична ветке "иначе" ниже)
                await this.showLevelMessage(this.state.currentBlockId, this.state.currentLevel, this.state.currentSpeedStep);

                const speedFactor = this.getSpeedFactor();
                if (speedFactor > 0) {
                    // Перед обратным отсчётом показываем ноты в статическом режиме
                    this.prepareBlockNotes(true);
                    try {
                        await startCountdown({ speedFactor });
                    } catch (error) {
                        // Если countdown был остановлен, прерываем запуск игры
                        if (error.message === 'Countdown stopped') {
                            return;
                        }
                        throw error;
                    }
                }

                this.playBackgroundTrack(this.state.currentBlockId);

                this.state.isPlaying = true;
                this.state.isGaming = true;
                this.updateGameUI(); // Показываем пульс

                setTimeout(() => {
                    // После отсчёта переинициализируем ноты в динамическом режиме
                    this.prepareBlockNotes(false);
                    initTuner(true);
                    // Воспроизведение нот отключено в игровом режиме
                    // const firstNote = getCurrentNote();
                    // if (firstNote) {
                    //     notePlayer.playNote(firstNote);
                    // }
                    playSequence();
                }, 500);

                // ВАЖНО: выходим, чтобы не выполнять сохранение прогресса и общий сброс ниже
                return;
            }

            // Для всех остальных случаев (включая переход с нулевого на первый уровень)
            // обновляем звезды и сохраняем прогресс через updateBlockStars
            if (this.state.currentBlockId !== null) {
                // Временно восстанавливаем accuracy для сохранения
                const savedAccuracy = this.state.accuracy;
                this.state.accuracy = accuracyToSave;
                this.updateBlockStars(this.state.currentBlockId, this.state.currentSpeedStep + 1);
                this.state.accuracy = savedAccuracy; // Возвращаем обратно
            }

            // Иначе увеличиваем уровень/скорость как обычно
            this.state.currentSpeedStep++;
            this.state.currentLevel++;

            // Проверяем, не достигли ли мы максимального уровня
            if (this.state.currentSpeedStep < this.speedSteps.length) {
                this.updateGameUI();
                // 3) Запускаем игру с текущего уровня (показываем сообщение уровня)
                await this.showLevelMessage(this.state.currentBlockId, this.state.currentLevel, this.state.currentSpeedStep);

                const speedFactor = this.getSpeedFactor();
                if (speedFactor > 0) {
                    // Перед обратным отсчётом показываем ноты в статическом режиме
                    this.prepareBlockNotes(true);
                    try {
                        await startCountdown({ speedFactor });
                    } catch (error) {
                        // Если countdown был остановлен, прерываем запуск игры
                        if (error.message === 'Countdown stopped') {
                            return;
                        }
                        throw error;
                    }
                }

                this.playBackgroundTrack(this.state.currentBlockId);

                // Возобновляем игровой режим и переключение нот
                this.state.isPlaying = true;
                this.state.isGaming = true;
                this.updateGameUI(); // Показываем пульс

                setTimeout(() => {
                    // После отсчёта переинициализируем ноты в динамическом режиме
                    this.prepareBlockNotes(false);
                    if (this.state.currentSpeedStep === 0) {
                        setListening(true);
                        resumeListening();
                    }
                    initTuner(true);
                    // Проигрываем первую ноту при запуске уровня
                    const firstNote = getCurrentNote();
                    if (firstNote) {
                        notePlayer.playNote(firstNote);
                    }
                    playSequence(); // Запускаем переключение нот снова
                }, 500);
            } else {
                // Все уровни пройдены - показываем сообщение о завершении
                await this.showCompletionMessage(this.state.currentBlockId);

                // Полностью сбрасываем состояние для этого блока
                blockCompleted = true;
                this.state.currentSpeedStep = 0;
                this.state.currentLevel = 0;
                this.state.correctCount = 0;
                this.state.totalCount = 0;
                this.state.accuracy = 0;
                this.state.isPlaying = false;
                this.state.isGaming = false;
            }
        } else {
            setCurrentNoteIndex(-1);
            
            this.state.accuracy = 0;
            this.state.correctCount = 0;
            this.updateGameUI();

            // Если пользователь выбрал откат прогресса на шаг назад
            if (shouldStepBack && this.state.currentBlockId !== null) {
                try {
                    const rolledBackLevel = await this.rollbackBlockProgress(this.state.currentBlockId);
                    // Устанавливаем текущий уровень на откатанный
                    this.state.currentSpeedStep = rolledBackLevel;
                    this.state.currentLevel = rolledBackLevel;
                    this.updateGameUI();
                } catch (error) {
                    console.error('Ошибка при откате прогресса:', error);
                }
            }

            // 3) Запускаем игру с текущего уровня (показываем сообщение уровня)
            await this.showLevelMessage(this.state.currentBlockId, this.state.currentLevel, this.state.currentSpeedStep);

            const speedFactor = this.getSpeedFactor();
            if (speedFactor > 0) {
                // Перед обратным отсчётом показываем ноты в статическом режиме
                this.prepareBlockNotes(true);
                try {
                    await startCountdown({ speedFactor });
                } catch (error) {
                    // Если countdown был остановлен, прерываем запуск игры
                    if (error.message === 'Countdown stopped') {
                        return;
                    }
                    throw error;
                }
            }

            this.playBackgroundTrack(this.state.currentBlockId);
            // Возобновляем игровой режим и переключение нот
            this.state.isPlaying = true;
            this.state.isGaming = true;
            this.updateGameUI(); // Показываем пульс

            setTimeout(() => {
                // После отсчёта переинициализируем ноты в динамическом режиме
                this.prepareBlockNotes(false);
                initTuner(true);
                // Проигрываем первую ноту при перезапуске уровня
                const firstNote = getCurrentNote();
                if (firstNote) {
                    notePlayer.playNote(firstNote);
                }
                playSequence(); // Запускаем переключение нот снова
            }, 500);
        }

        // Сохраняем прогресс только если не было отката (откат уже сохранил прогресс)
        if (!shouldStepBack) {
            // Вычисляем время, проведенное в игровой сессии (в секундах)
            let playTimeSeconds = 0;
            if (this.sessionStartTime) {
                playTimeSeconds = Math.floor((Date.now() - this.sessionStartTime) / 1000);
                this.sessionStartTime = null; // Сбрасываем после использования
            }
            // Временно восстанавливаем accuracy для сохранения
            const savedAccuracy = this.state.accuracy;
            this.state.accuracy = accuracyToSave;
            // updateBestSpeedLevel = false, потому что best_speed_level уже обновлен в updateBlockStars
            // Здесь мы только сохраняем финальное состояние (completed, playTimeSeconds)
            await this.saveProgress({ completed: blockCompleted, playTimeSeconds, updateBestSpeedLevel: false });
            this.state.accuracy = savedAccuracy; // Возвращаем обратно
        }

        resetCurrentNoteId();

        if (this.state.currentBlockId !== null) {
            highlightMelodyBlock(this.state.currentBlockId);
        }
    }

    async stopGameSequence() {
        // Останавливаем все таймеры и сбрасываем состояние
        stopSequence();

        // Сбрасываем состояние игры
        this.state.isPlaying = false;
        this.state.isGaming = false;

        // Обновляем UI чтобы скрыть пульс
        this.updateGameUI();

        // Выходим из полноэкранного режима при остановке игры
        exitFullscreen().catch(err => {
            console.warn('Не удалось выйти из полноэкранного режима:', err);
        });

        // Сбрасываем все состояния обнаружения нот
        resetCurrentNoteId();
        resetNoteDetection();

        // Ждем немного чтобы убедиться, что все остановилось
        await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Сообщение о результате уровня
    // Если askForNextLevel = true (успешный нулевой уровень), на этом же окне
    // спрашиваем, готов ли пользователь перейти на первый уровень.
    // Если askForStepBack = true (неудачное прохождение уровня > 0), показываем кнопку отката.
    async showResultMessage(accuracy, { askForNextLevel = false, askForStepBack = false } = {}) {
        return new Promise((resolve) => {
            // Останавливаем переключение нот перед показом сообщения
            stopSequence();

            const messageContainer = document.createElement('div');
            messageContainer.className = 'game-result-overlay';
            messageContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            font-size: 48px;
            color: white;
            font-weight: bold;
            text-align: center;
            flex-direction: column;
        `;

            const resultHtml = `
            <div class="game-message-content">${translate('Результат:')} ${accuracy}%</div>
            <div class="game-message-content" style="font-size: 24px; margin-top: 20px;">${translate('Попаданий:')} ${this.state.correctCount}/${this.getNotesCount()}</div>
        `;

            messageContainer.innerHTML = resultHtml;

            if (askForNextLevel) {
                const question = document.createElement('div');
                question.className = 'game-message-content';
                question.textContent = translate('Вы успешно прошли нулевой уровень. Перейти к первому уровню (с ритмом)?');
                question.style.fontSize = '24px';
                question.style.marginTop = '30px';

                const buttonsWrapper = document.createElement('div');
                buttonsWrapper.style.marginTop = '25px';
                buttonsWrapper.style.display = 'flex';
                buttonsWrapper.style.gap = '20px';
                buttonsWrapper.style.justifyContent = 'center';

                const stayButton = document.createElement('button');
                stayButton.className = 'game-message-content';
                stayButton.textContent = translate('Остаться на нулевом уровне');
                stayButton.style.padding = '10px 20px';
                stayButton.style.fontSize = '20px';
                stayButton.style.cursor = 'pointer';

                const nextButton = document.createElement('button');
                nextButton.className = 'game-message-content';
                nextButton.textContent = translate('Перейти на первый уровень');
                nextButton.style.padding = '10px 20px';
                nextButton.style.fontSize = '20px';
                nextButton.style.cursor = 'pointer';

                stayButton.addEventListener('click', () => {
                    if (messageContainer.parentNode) {
                        messageContainer.parentNode.removeChild(messageContainer);
                    }
                    resolve({ proceedToNextLevel: false });
                });

                nextButton.addEventListener('click', () => {
                    if (messageContainer.parentNode) {
                        messageContainer.parentNode.removeChild(messageContainer);
                    }
                    resolve({ proceedToNextLevel: true });
                });

                buttonsWrapper.appendChild(stayButton);
                buttonsWrapper.appendChild(nextButton);

                messageContainer.appendChild(question);
                messageContainer.appendChild(buttonsWrapper);
            } else if (askForStepBack) {
                const question = document.createElement('div');
                question.className = 'game-message-content';
                question.textContent = translate('Вернуться на предыдущий уровень сложности?');
                question.style.fontSize = '24px';
                question.style.marginTop = '30px';

                const buttonsWrapper = document.createElement('div');
                buttonsWrapper.className = 'game-message-content';
                buttonsWrapper.style.marginTop = '25px';
                buttonsWrapper.style.display = 'flex';
                buttonsWrapper.style.gap = '20px';
                buttonsWrapper.style.justifyContent = 'center';

                const stepBackButton = document.createElement('button');
                stepBackButton.className = 'game-message-content';
                stepBackButton.textContent = translate('Вернуться на шаг назад');
                stepBackButton.style.padding = '10px 20px';
                stepBackButton.style.fontSize = '20px';
                stepBackButton.style.cursor = 'pointer';
                stepBackButton.style.backgroundColor = '#ff4444';

                stepBackButton.addEventListener('click', () => {
                    if (messageContainer.parentNode) {
                        messageContainer.parentNode.removeChild(messageContainer);
                    }
                    resolve({ stepBack: true });
                });

                buttonsWrapper.appendChild(stepBackButton);

                messageContainer.appendChild(question);
                messageContainer.appendChild(buttonsWrapper);
                
                // Автоматически закрываем окно через 5 секунд, если пользователь не нажал кнопку
                setTimeout(() => {
                    if (messageContainer.parentNode) {
                        messageContainer.parentNode.removeChild(messageContainer);
                    }
                    resolve({ stepBack: false });
                }, 5000); // 5 секунд
            }

            document.body.appendChild(messageContainer);

            // Если не нужно спрашивать пользователя – просто закрываем окно через 5 секунд
            if (!askForNextLevel && !askForStepBack) {
                setTimeout(() => {
                    if (messageContainer.parentNode) {
                        messageContainer.parentNode.removeChild(messageContainer);
                    }
                    resolve({ proceedToNextLevel: null, stepBack: false });
                }, 5000); // 5 секунд
            }
        });
    }

// Показать сообщение уровня
    async showLevelMessage(blockId, level, speedStep) {
        return new Promise((resolve) => {
            // Останавливаем переключение нот перед показом сообщения
            stopSequence();

            const messageContainer = document.createElement('div');
            messageContainer.className = 'game-level-overlay';
            messageContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            font-size: 48px;
            color: white;
            font-weight: bold;
            text-align: center;
            flex-direction: column;
        `;

            const levels = [
                translate('Без длительности'), 
                translate('25% скорости'), 
                translate('50% скорости'), 
                translate('75% скорости'), 
                translate('100% скорости')
            ];
            messageContainer.innerHTML = `
            <div class="game-message-content">${translate('Блок')} ${blockId}</div>
            <div class="game-message-content">${translate('Уровень')} ${level} | ${levels[speedStep]}</div>
        `;

            document.body.appendChild(messageContainer);

            setTimeout(() => {
                document.body.removeChild(messageContainer);
                resolve();
            }, 3000);
        });
    }

    // Новый метод для показа сообщения о завершении всех уровней
    async showCompletionMessage(blockId) {
        return new Promise((resolve) => {
            // Останавливаем переключение нот перед показом сообщения
            stopSequence();

            const messageContainer = document.createElement('div');
            messageContainer.className = 'game-completion-overlay';
            messageContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            font-size: 48px;
            color: white;
            font-weight: bold;
            text-align: center;
            flex-direction: column;
        `;

            messageContainer.innerHTML = `
            <div class="game-message-content">${translate('Поздравляем!')}</div>
            <div class="game-message-content">${translate('Блок')} ${blockId} ${translate('полностью пройден!')}</div>
            <div class="game-message-content" style="font-size: 24px; margin-top: 20px;">${translate('Все уровни завершены')}</div>
        `;

            document.body.appendChild(messageContainer);

            setTimeout(() => {
                document.body.removeChild(messageContainer);
                resolve();
            }, 5000); // 5 секунд
        });
    }

    // Обработка обнаруженной ноты
    // game.js - обновленный handleDetectedNote
    async handleDetectedNote(currentIndex) {
        if (!this.state.isGaming) return;

        const Measure = this.getSequence();

        // Защита от повторного счета: игнорируем только уже обработанные ноты
        if (currentIndex < this.state.correctCount) {
            return false;
        }

        // Получаем текущую ноту ДО увеличения счетчика
        const currentNote = getCurrentNote();

        // Проверяем, является ли текущий элемент паузой
        const isPause = Array.isArray(currentNote) && currentNote[0] && currentNote[0].pause === "true";
        
        // Защита от распознавания остаточного звука после пропуска паузы
        // Теперь за это отвечает mustAwaitSilence в tuner.js - он физически блокирует распознавание до тишины

        // Если это пауза
        if (isPause) {
            const speedFactor = this.getSpeedFactor();
            
            // В нулевом режиме сложности паузы пропускаются - переключаем индекс
            if (speedFactor === 0) {
                await nextNote();
                
                // Пропускаем все следующие паузы подряд
                let currentNote = getCurrentNote();
                while (currentNote && Array.isArray(currentNote) && currentNote[0] && currentNote[0].pause === "true") {
                    await nextNote();
                    currentNote = getCurrentNote();
                }
                
                // Обновляем отображение нот на грифе после пропуска пауз
                if (this.state.isGaming) {
                    threeJSBand.layoutStaticGameNotes?.();
                }
                
                // Возвращаем true, чтобы показать, что пауза была пропущена
                return true;
            }
            
            // На других уровнях паузы обрабатываются автоматически через playSequence()
            return true;
        }

        // Нормализуем структуру данных ноты/аккорда (иногда вложенные массивы)
        const flattenNotes = (input) => {
            if (!input) return [];
            if (!Array.isArray(input)) {
                return [input];
            }

            const queue = [...input];
            const result = [];

            while (queue.length > 0) {
                const item = queue.shift();
                if (!item) continue;

                if (Array.isArray(item)) {
                    queue.unshift(...item);
                } else {
                    result.push(item);
                }
            }

            return result;
        };

        const notesToProcess = flattenNotes(currentNote).filter(note =>
            note && note.string !== undefined && note.fret !== undefined
        );

        // В строгом ритмическом режиме (скорость > 0) засчитываем попадание
        // только если ВСЕ ноты текущего аккорда/ноты реально попали по таймингу
        let allNotesHitInWindow = true;

        // Отмечаем ноты в threeJSBand с учетом игрового режима
        if (this.state.isGaming && notesToProcess.length > 0) {
            const speedFactor = this.getSpeedFactor();

            if (speedFactor > 0) {
                // Строгий режим Guitar Hero
                for (const note of notesToProcess) {
                    const hit = threeJSBand.markNoteAsHit(note.string, note.fret);
                    if (!hit) {
                        allNotesHitInWindow = false;
                        break;
                    }
                }

                // Если хотя бы одна нота аккорда не попала в своё окно — не считаем попытку
                if (!allNotesHitInWindow) {
                    return false;
                }
            } else {
                // Нулевой уровень: как раньше — важна только высота, а не точный тайминг
                notesToProcess.forEach(note => {
                    threeJSBand.markNoteAsHit(note.string, note.fret);
                });

                // В нулевом режиме удаляем текущие ноты с грифа сразу после попадания
                notesToProcess.forEach(note => {
                    threeJSBand.removeNoteFromFretboard(note.string, note.fret);
                });

                threeJSBand.layoutStaticGameNotes?.();
            }
        }

        // Если мы здесь, значит либо не игровой режим, либо все ноты были успешно сыграны
        this.state.correctCount++;

        // "Удар сердца" на кардиографе при каждом попадании ноты
        this.triggerHeartbeatJump();

        // Обновляем точность (используем количество нот без пауз)
        const notesCount = this.getNotesCount();
        this.state.accuracy = Math.min(100, Math.round((this.state.correctCount / notesCount) * 100));
        this.updateGameUI();

        // Проверяем завершение последовательности (сравниваем с количеством нот без пауз)
        if (this.state.correctCount >= notesCount) {
            await this.checkLevelCompletion();
            return true;
        }



        // Подсказка только в нулевом режиме: перейти к следующей ноте
        if (this.getSpeedFactor() === 0) {
            await nextNote();
            
            // Проверяем, не является ли следующая нота паузой
            const nextNoteData = getCurrentNote();
            if (nextNoteData && Array.isArray(nextNoteData) && nextNoteData[0] && nextNoteData[0].pause === "true") {
                // Если следующая нота - пауза, пропускаем её и переходим к следующей
                await nextNote();
                
                // Проверяем рекурсивно, не является ли следующая нота тоже паузой
                const nextNextNoteData = getCurrentNote();
                if (nextNextNoteData && Array.isArray(nextNextNoteData) && nextNextNoteData[0] && nextNextNoteData[0].pause === "true") {
                    // Рекурсивно пропускаем все паузы подряд
                    let currentNote = getCurrentNote();
                    while (currentNote && Array.isArray(currentNote) && currentNote[0] && currentNote[0].pause === "true") {
                        await nextNote();
                        currentNote = getCurrentNote();
                    }
                }
                
                // Обновляем отображение нот на грифе
                if (this.state.isGaming) {
                    threeJSBand.layoutStaticGameNotes?.();
                }
                
                // Сбрасываем состояние tuner и требуем тишину перед новой нотой
                resetNoteDetection();
            }

            // Воспроизведение нот отключено в игровом режиме
            // const nextNoteData = getCurrentNote();
            // if (nextNoteData) {
            //     notePlayer.playNote(nextNoteData);
            // }
        }

        return true;
    }

    // Инициализация canvas для кардиографа
    initHeartbeatCanvas() {
        const levelInfo = document.getElementById('level-info');
        if (!levelInfo) return;

        const canvas = document.getElementById('heartbeat-canvas');
        if (!canvas) return;

        this.heartbeatCanvas = canvas;
        this.heartbeatCtx = canvas.getContext('2d');
        
        // Устанавливаем размеры canvas
        const resizeCanvas = () => {
            const wrapper = canvas.parentElement;
            if (wrapper) {
                canvas.width = wrapper.offsetWidth;
                canvas.height = wrapper.offsetHeight || 54;
            }
        };
        
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        
        // Запускаем анимацию
        this.startHeartbeatAnimation();
    }

    // Запуск анимации кардиографа
    startHeartbeatAnimation() {
        if (this.heartbeatAnimation) {
            cancelAnimationFrame(this.heartbeatAnimation);
        }
        
        const animate = () => {
            this.drawHeartbeat();
            this.heartbeatAnimation = requestAnimationFrame(animate);
        };
        
        animate();
    }

    // Отрисовка кардиографа (как в видео)
    drawHeartbeat() {
        if (!this.heartbeatCanvas || !this.heartbeatCtx) return;

        const ctx = this.heartbeatCtx;
        const width = this.heartbeatCanvas.width;
        const height = this.heartbeatCanvas.height;
        const centerY = height / 2;
        const maxJumpAmplitude = Math.min(height * 0.3, 20); // Максимальная амплитуда удара

        // Очищаем canvas
        ctx.clearRect(0, 0, width, height);

        // Рисуем белую сетку на фоне (шаг 30px, прозрачность 5%)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        
        // Вертикальные линии
        for (let x = 0; x <= width; x += 30) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, height);
            ctx.stroke();
        }
        
        // Горизонтальные линии
        for (let y = 0; y <= height; y += 30) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(width, y);
            ctx.stroke();
        }

        // Скорость движения точки в соответствии со скоростью игры
        // В режиме playAll используем базовую скорость
        const isPlayAllMode = window.isPlayAllMode && window.app && window.app.playAllStats && window.app.playAllStats.isTracking;
        const speedFactor = isPlayAllMode ? 1 : this.getSpeedFactor();
        let dotSpeed;
        if (speedFactor === 0) {
            dotSpeed = 0.5; // медленно для статичного режима
        } else {
            // От 1 (0.25x) до 4 (1x)
            dotSpeed = 1 + 3 * speedFactor;
        }

        // Обновляем позицию точки
        this.heartbeatDotPosition += dotSpeed;
        if (this.heartbeatDotPosition > width + 20) {
            this.heartbeatDotPosition = -20;
            // Сбрасываем историю при перезапуске
            this.heartbeatWaveHistory = [];
        }

        // Обработка "удара сердца" при угадывании ноты
        let jumpOffset = 0;
        if (this.heartbeatJumpStartTime !== null) {
            const elapsed = Date.now() - this.heartbeatJumpStartTime;
            const jumpDuration = 50; // 10мс на полный S-образный цикл
            
            if (elapsed >= jumpDuration) {
                // Прыжок завершен
                this.heartbeatJumpStartTime = null;
                this.heartbeatJumpAmplitude = 0;
            } else {
                // Вычисляем фазу от 0 до 1 за 10мс
                const phase = elapsed / jumpDuration;
                // S-образная синусоида: выше уровня → ниже уровня → возврат на уровень
                // Полный цикл от 0 до 2π: 0 → +amplitude → 0 → -amplitude → 0
                const jumpPhase = phase * 2 * Math.PI;
                jumpOffset = Math.sin(jumpPhase) * this.heartbeatJumpAmplitude;
            }
        }

        // Добавляем текущую позицию в историю (для отрисовки следа)
        const dotX = this.heartbeatDotPosition;
        const dotY = centerY - jumpOffset;
        
        this.heartbeatWaveHistory.push({ x: dotX, y: dotY });
        
        // Ограничиваем размер истории (храним только видимую часть)
        const historyLimit = width + 50;
        this.heartbeatWaveHistory = this.heartbeatWaveHistory.filter(point => 
            point.x > dotX - historyLimit && point.x <= dotX + 20
        );

        // Рисуем линию кардиографа (красная линия, оставляющая след)
        // С эффектом размытия/прозрачности у краев (в пределах 50px)
        const fadeDistance = 50;
        ctx.lineWidth = 2;
        ctx.beginPath();
        
        let pathStarted = false;
        const gapSize = 20; // Разрыв перед точкой
        
        for (let i = 0; i < this.heartbeatWaveHistory.length; i++) {
            const point = this.heartbeatWaveHistory[i];
            
            // Не рисуем линию перед точкой (разрыв ~20px)
            if (point.x > dotX && point.x <= dotX + gapSize) {
                if (pathStarted) {
                    ctx.stroke();
                    ctx.beginPath();
                    pathStarted = false;
                }
                continue;
            }
            
            // Вычисляем прозрачность в зависимости от расстояния до краев
            const distanceToLeft = point.x;
            const distanceToRight = width - point.x;
            const minDistance = Math.min(distanceToLeft, distanceToRight);
            
            let alpha = 1.0;
            if (minDistance < fadeDistance) {
                // Плавное увеличение прозрачности (уменьшение alpha) при приближении к краю
                alpha = Math.max(0, minDistance / fadeDistance);
            }
            
            // Устанавливаем цвет с учетом прозрачности
            ctx.strokeStyle = `rgba(255, 82, 82, ${alpha})`;
            
            if (!pathStarted) {
                ctx.moveTo(point.x, point.y);
                pathStarted = true;
            } else {
                ctx.lineTo(point.x, point.y);
            }
        }
        
        if (pathStarted) {
            ctx.stroke();
        }

        // Рисуем размытую точку без границы (уменьшена на 30%)
        // Вычисляем прозрачность маркера в зависимости от расстояния до краев
        const distanceToLeft = dotX;
        const distanceToRight = width - dotX;
        const minDistance = Math.min(distanceToLeft, distanceToRight);
        
        let markerAlpha = 1.0;
        if (minDistance < fadeDistance) {
            // Плавное увеличение прозрачности (уменьшение alpha) при приближении к краю
            markerAlpha = Math.max(0, minDistance / fadeDistance);
        }
        
        // Используем shadowBlur для размытия
        ctx.shadowBlur = 10.5; // 15 * 0.7
        ctx.shadowColor = `rgba(255, 255, 255, ${0.8 * markerAlpha})`;
        
        // Светлая точка с градиентом (уменьшена на 30%)
        const gradient = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, 8.4); // 12 * 0.7
        gradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 * markerAlpha})`);
        gradient.addColorStop(0.4, `rgba(255, 200, 200, ${0.6 * markerAlpha})`);
        gradient.addColorStop(0.7, `rgba(255, 150, 150, ${0.3 * markerAlpha})`);
        gradient.addColorStop(1, 'rgba(255, 82, 82, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(dotX, dotY, 8.4, 0, Math.PI * 2); // 12 * 0.7
        ctx.fill();

        // Яркое ядро точки (уменьшено на 30%)
        ctx.shadowBlur = 5.6; // 8 * 0.7
        ctx.shadowColor = `rgba(255, 255, 255, ${0.6 * markerAlpha})`;
        ctx.fillStyle = `rgba(255, 255, 255, ${0.8 * markerAlpha})`;
        ctx.beginPath();
        ctx.arc(dotX, dotY, 2.8, 0, Math.PI * 2); // 4 * 0.7
        ctx.fill();
        
        // Сбрасываем shadowBlur
        ctx.shadowBlur = 0;
    }

    // Вызов "удара сердца" при угадывании ноты
    triggerHeartbeatJump() {
        // Если предыдущий прыжок ещё не завершился, не запускаем новый
        if (this.heartbeatJumpStartTime !== null) {
            return;
        }

        // Генерируем случайную амплитуду удара (от 10 до максимальной)
        const maxAmplitude = Math.min(
            (this.heartbeatCanvas?.height || 54) * 0.3,
            20
        );
        this.heartbeatJumpAmplitude = 10 + Math.random() * (maxAmplitude - 10);
        
        // Запускаем быстрый прыжок (100мс): запоминаем время начала
        this.heartbeatJumpStartTime = Date.now();
    }

    // Обновление игрового интерфейса
    updateGameUI() {
        const levelInfo = document.getElementById('level-info');
        if (!levelInfo) return;

        const heartbeatWrapper = levelInfo.querySelector('.heartbeat-2');
        if (!heartbeatWrapper) return;

        // 1) Скрываем пульс, если нет игрового процесса
        // Проверяем как обычный игровой режим, так и режим "проиграть все"
        const isPlayAllMode = window.isPlayAllMode && window.app && window.app.playAllStats && window.app.playAllStats.isTracking;
        if (!this.state.isPlaying && !this.state.isGaming && !isPlayAllMode) {
            levelInfo.style.display = 'none';
            if (this.heartbeatAnimation) {
                cancelAnimationFrame(this.heartbeatAnimation);
                this.heartbeatAnimation = null;
            }
            // Сбрасываем позицию точки и состояние
            this.heartbeatDotPosition = 0;
            this.heartbeatJumpStartTime = null;
            this.heartbeatJumpAmplitude = 0;
            this.heartbeatWaveHistory = [];
            return;
        }

        // 2) Показываем пульс на всю ширину контейнера во время игры
        levelInfo.style.display = 'flex';
        heartbeatWrapper.style.width = '100%';

        // Инициализируем canvas, если еще не инициализирован
        if (!this.heartbeatCanvas) {
            this.initHeartbeatCanvas();
        } else {
            // Обновляем размеры canvas
            const wrapper = this.heartbeatCanvas.parentElement;
            if (wrapper) {
                this.heartbeatCanvas.width = wrapper.offsetWidth;
                this.heartbeatCanvas.height = wrapper.offsetHeight || 54;
            }
            // Если анимация остановлена (после перезапуска блока) — перезапускаем её
            if (!this.heartbeatAnimation) {
                this.startHeartbeatAnimation();
            }
        }
    }

    // Начать игру с определенного блока
    // game.js - обновленный startGameFromBlock
    // game.js - обновленный метод startGameFromBlock
    async startGameFromBlock(blockId) {
        // Устанавливаем запрошенный блок ID для отслеживания прерываний
        this.requestedBlockId = blockId;
        
        // Создаем новый AbortController для этого запуска блока
        // Если был предыдущий - он уже отменен, создаем новый
        this.startBlockAbortController = new AbortController();
        const abortSignal = this.startBlockAbortController.signal;
        
        // Если уже есть запущенный процесс запуска блока, прерываем его
        if (this.currentStartBlockPromise) {
            // Просто продолжаем - новый блок перезапишет requestedBlockId и создаст новый AbortController
        }
        
        // Сохраняем промис текущего запуска
        const startPromise = (async () => {
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }
            
            // Внутренняя остановка предыдущего блока - НЕ прерываем текущий процесс
            // (stopBlockPlayback(false) не должен трогать AbortController)
            await stopBlockPlayback(false);
            
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }
            
            const blockMeasures = getMeasuresForBlock(blockId);

            if (!blockMeasures || blockMeasures.length === 0) {
                alert(translate('Не удалось загрузить данные для этого блока!'));
                return;
            }

            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            // Загружаем настройки эффектов для этого блока
            if (guitarEffects && guitarEffects.loadEffectsFromServer) {
                await guitarEffects.loadEffectsFromServer(this.songId, blockId);
            }
            
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            // Определяем стартовый уровень сложности для блока ДО показа сообщения
            const savedProgress = this.blockProgress[blockId];
            const minBlockLevel = getBlockMinGameLevel(blockId) || 0;

            let baseSpeedStep;
            if (savedProgress && typeof savedProgress.best_speed_level === 'number') {
                // Начинаем со следующего непройденного уровня (best_speed_level + 1)
                // Если пользователь прошёл уровень 0, начинаем с уровня 1
                baseSpeedStep = savedProgress.best_speed_level + 1;
            } else {
                baseSpeedStep = minBlockLevel;
            }

            // Не опускаемся ниже минимального уровня для блока
            baseSpeedStep = Math.max(minBlockLevel, baseSpeedStep);
            // Если вышли за максимальный уровень, начинаем с максимального (повторное прохождение)
            const startSpeedStep = Math.min(baseSpeedStep, this.speedSteps.length - 1);

            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            this.state.currentBlockId = blockId;
            this.state.currentSpeedStep = startSpeedStep;
            this.state.currentLevel = startSpeedStep;
            this.state.correctCount = 0;
            this.state.totalCount = 0;
            this.state.accuracy = 0;

            // 1) Останавливаем игру (сделано выше через stopBlockPlayback)
            // 2) Проигрываем ноты с оригинальной скоростью (НЕ игровой режим)
            this.state.isGaming = false;
            
            // Убеждаемся, что флаг isPlayAllMode сброшен перед запуском блока
            // Это критически важно для работы синтезатора и фоновой музыки
            window.isPlayAllMode = false;
            
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }
            
            // Предпрослушка блока перед игрой.
            // Если во время проигрывания пользователь нажал STOP (handleStopPlay -> stopBlockPlayback),
            // playBlock отклонит промис с ошибкой 'Playback stopped manually'.
            // В этом случае мы должны ПОЛНОСТЬЮ прервать дальнейшее выполнение startGameFromBlock
            // и НЕ запускать игру на этом блоке.
            try {
                await playBlock(blockId);
            } catch (error) {
                if (error && error.message === 'Playback stopped manually') {
                    // Нормальное принудительное прерывание – выходим из запуска блока целиком
                    return;
                }
                throw error;
            }
            
            // КРИТИЧЕСКИ ВАЖНО: После предпрослушки останавливаем все звуки и очищаем таймауты
            // Это предотвращает воспроизведение звуков из предпрослушки с задержкой во время игры
            await stopBlockPlayback(false);

            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            // 3) Показываем информацию о текущем блоке и скорости
            await this.showLevelMessage(blockId, this.state.currentLevel, this.state.currentSpeedStep);
            
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            setCurrentNoteIndex(-1);
            stopSequence();

            // Сбрасываем ВСЕ состояния обнаружения нот
            resetCurrentNoteId();
            resetNoteDetection();
            setListening(true);

            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            this.state.blockMeasures = blockMeasures;
            // Включаем паузы в последовательность для игры, чтобы они учитывались при автоматическом переключении нот
            this.state.measures = blockMeasures.flat();
            this.state.currentMeasureIndex = 0;

            setActiveMelodyBlock(blockId);

            // НЕ запускаем фоновую музыку до окончания отсчета
            // this.playBackgroundTrack(blockId);

            // Передаем коэффициент скорости в threeJSBand
            window.gameSpeedFactor = this.getSpeedFactor();

            // Переводим систему в игровой режим до генерации нот
            this.state.isPlaying = true;
            this.state.isGaming = true;
            this.updateGameUI(); // Показываем пульс

            // Фиксируем время начала игровой сессии
            this.sessionStartTime = Date.now();

            // Входим в полноэкранный режим при старте игры
            enterFullscreen().catch(err => {
                console.warn('Не удалось войти в полноэкранный режим:', err);
                // Ошибка уже отправлена на сервер в enterFullscreen, но логируем для контекста
                errorLogger.logError({
                    error_type: 'fullscreen_error',
                    message: `Не удалось войти в полноэкранный режим при старте игры: ${err.message || String(err)}`,
                    stack: err.stack,
                    url: window.location.href,
                    additional_info: {
                        error_name: err.name,
                        context: 'game_start',
                        block_id: this.state.currentBlockId
                    }
                });
            });

            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            // Подготавливаем ноты для игры в статичном режиме (отображаются сразу, но без движения)
            this.prepareBlockNotes(true);

            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            // 4) Запускаем обратный отсчет (только звук, без окна) перед началом игры (кроме нулевого уровня)
            const speedFactor = this.getSpeedFactor();
            if (speedFactor > 0) {
                try {
                    await startCountdown({ speedFactor });
                } catch (error) {
                    // Если countdown был остановлен, прерываем запуск игры
                    if (error.message === 'Countdown stopped') {
                        return;
                    }
                    throw error;
                }
            }
            
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }

            // После окончания отсчета запускаем игру:
            // 1) Запускаем фоновую музыку
            this.playBackgroundTrack(blockId);
            
            // Проверяем, не был ли запрошен другой блок или не был ли отменен через AbortController
            if (this.requestedBlockId !== blockId || abortSignal.aborted) {
                return; // Прерываем выполнение
            }
            
            // 2) Пересчитываем ноты для движения (динамичный режим)
            this.prepareBlockNotes(false);

            // Проверяем, не был ли запрошен другой блок
            if (this.requestedBlockId !== blockId) {
                return; // Прерываем выполнение
            }

            initTuner(true);

            // Воспроизведение нот отключено в игровом режиме
            // const firstNote = getCurrentNote();
            // if (firstNote) {
            //     notePlayer.playNote(firstNote);
            // }

            playSequence();
        })();
        
        this.currentStartBlockPromise = startPromise;
        try {
            await startPromise;
        } finally {
            // Очищаем промис и AbortController после завершения (нормального или прерванного)
            this.currentStartBlockPromise = null;
            // Очищаем AbortController только если это был наш (не был заменен новым)
            if (this.startBlockAbortController === abortSignal) {
                this.startBlockAbortController = null;
            }
        }
    }

    prepareBlockNotes(staticMode = false) {
        if (!threeJSBand) {
            return;
        }

        const blockMeasures = this.state.blockMeasures;
        if (!blockMeasures || blockMeasures.length === 0) {
            threeJSBand.clearAllMovingNotes();
            return;
        }

        const allBlockNotes = [];
        blockMeasures.forEach(measure => {
            measure.forEach(chordGroup => {
                const isPause = chordGroup[0] && chordGroup[0].pause === "true";
                if (!isPause) {
                    chordGroup.forEach(noteData => {
                        if (noteData && noteData.noteName) {
                            allBlockNotes.push(noteData);
                        }
                    });
                }
            });
        });

        threeJSBand.clearAllMovingNotes();

        if (allBlockNotes.length > 0) {
            threeJSBand.animateCameraToBlock(allBlockNotes);
        }

        const currentTime = Tone.now();
        const tempo = getCurrentTempo() || 120;
        const speedFactor = this.getSpeedFactor();

        if (speedFactor === 0) {
            threeJSBand.addStaticBlockNotes(blockMeasures, tempo);
            return;
        }

        // Если статичный режим - показываем ноты на их начальных позициях (вдали от грифа) без движения
        if (staticMode) {
            // Подготавливаем ноты с правильными позициями (как перед игрой), но "замораживаем" их
            // Используем тот же алгоритм, что и для динамичного режима, но с isWaiting = true
            
            // Минимальное время полета для каждой ноты
            const MIN_NOTE_TRAVEL_TIME_COUNTDOWN = 1.5;
            
            // Сбрасываем счетчик для новых нот
            window._noteDebugCounter = 0;
            
            let timeCursor = 0;
            
            blockMeasures.forEach(measure => {
                measure.forEach(chordGroup => {
                    const isPause = chordGroup[0] && chordGroup[0].pause === "true";
                    if (!isPause) {
                        const durationValue = chordGroup[0].duration;
                        const isDotted = chordGroup[0].dotted;

                        const stepDuration = 4 / durationValue;
                        const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;

                        // Время прилета к грифу С учетом speedFactor для синхронизации со звуком
                        // speedFactor применяется ко ВСЕМУ timeCursor, поэтому интервалы пропорциональны
                        let groupTargetTime = currentTime + (timeCursor * (60 / tempo) / speedFactor);
                        
                        // Для countdown режима НЕ корректируем первую ноту!
                        // initialZ уже правильно рассчитан для визуального расстояния
                        
                        // Каждая нота должна лететь MIN_NOTE_TRAVEL_TIME_COUNTDOWN
                        // Рассчитываем startTime для этой группы нот
                        const groupNoteStartTime = groupTargetTime - MIN_NOTE_TRAVEL_TIME_COUNTDOWN;

                        chordGroup.forEach(noteData => {
                            // Добавляем ноты с правильными позициями
                            // Каждая группа нот имеет свой startTime, рассчитанный из targetTime
                            const note = threeJSBand.addMovingNote(
                                noteData,
                                groupNoteStartTime, // Время создания = targetTime - MIN_NOTE_TRAVEL_TIME
                                groupTargetTime, // targetTime - момент достижения грифа
                                false, // activated = false для countdown (используем actualStartTime для initialZ)
                                null
                            );
                            if (note) {
                                // Помечаем ноты как ожидающие, чтобы они не обновлялись в updateMovingNotes
                                note.isWaiting = true;
                                // Сохраняем правильное targetTime (оно уже сохранено в originalTargetTime в addMovingNote)
                                // Устанавливаем targetTime в MAX_SAFE_INTEGER чтобы движение не началось
                                // originalTargetTime содержит правильное время для будущего использования после отсчета
                                note.targetTime = Number.MAX_SAFE_INTEGER; // Отключаем движение - progress останется 0
                                note.progress = 0; // Прогресс 0 - ноты на начальных позициях (initialZ)
                                // Важно: startTime оставляем как currentTime, initialZ уже правильно рассчитан
                                // originalTargetTime уже сохранен в addMovingNote и содержит groupTargetTime
                                // Сохраняем groupTargetTime для группировки первой группы нот
                                note._staticGroupTime = groupTargetTime;
                            }
                        });

                        timeCursor += actualDuration;
                    } else {
                        const pauseDuration = chordGroup[0].duration;
                        const isDotted = chordGroup[0].dotted || false;

                        const stepDuration = 4 / pauseDuration;
                        const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;
                        timeCursor += actualDuration;
                    }
                });
            });

            // После добавления всех нот обновляем hammer эффекты
            if (threeJSBand) {
                threeJSBand.updateHammerEffects();
                
                // Создаем маркеры пальцев для первой группы нот (первой по времени)
                // Находим минимальное _staticGroupTime среди всех нот в статичном режиме
                const allStaticNotes = threeJSBand.movingNotes.filter(n => n._staticGroupTime !== undefined);
                if (allStaticNotes.length > 0) {
                    const firstGroupTime = Math.min(...allStaticNotes.map(n => n._staticGroupTime));
                    const firstGroupNotes = allStaticNotes.filter(n => 
                        n._staticGroupTime === firstGroupTime &&
                        !n.fingerSphereCreated &&
                        n.noteData &&
                        n.noteData.finger !== undefined &&
                        n.noteData.finger !== null
                    );
                    
                    // Создаем маркеры для первой группы нот
                    firstGroupNotes.forEach(note => {
                        const position = threeJSBand.fretStringPositions[note.fret];
                        if (position && position[note.string - 1]) {
                            const pos = position[note.string - 1];
                            const fingerCircle = threeJSBand.createFingerCircle(
                                note.noteData.finger,
                                pos.x,
                                pos.y - 8,
                                note.string
                            );
                            if (fingerCircle) {
                                fingerCircle.visible = true;
                                threeJSBand.scene.add(fingerCircle);
                                note.fingerSphere = fingerCircle;
                                note.fingerSphereCreated = true;
                                threeJSBand.fingerSpheres.push({
                                    sphere: fingerCircle,
                                    string: note.string,
                                    fret: note.fret,
                                    finger: note.noteData.finger
                                });
                            }
                        }
                    });
                }
            }
            return;
        }

        // Динамичный режим - ноты будут двигаться
        
        // Минимальное время полета для каждой ноты
        const MIN_NOTE_TRAVEL_TIME_PREVIEW = 1.5;
        
        // Сбрасываем счетчик для новых нот
        window._noteDebugCounter = 0;
        
        let timeCursor = 0;
        blockMeasures.forEach(measure => {
            measure.forEach(chordGroup => {
                const isPause = chordGroup[0] && chordGroup[0].pause === "true";
                if (!isPause) {
                    const durationValue = chordGroup[0].duration;
                    const isDotted = chordGroup[0].dotted;

                    const stepDuration = 4 / durationValue;
                    const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;

                    // Время прилета к грифу С учетом speedFactor для синхронизации со звуком
                    // speedFactor применяется ко ВСЕМУ timeCursor, поэтому интервалы пропорциональны
                    let groupTargetTime = currentTime + (timeCursor * (60 / tempo) / speedFactor);
                    
                    // НЕ корректируем первую ноту! initialZ уже правильно рассчитан
                    
                    // Каждая нота должна лететь MIN_NOTE_TRAVEL_TIME_PREVIEW
                    // Рассчитываем startTime для этой группы нот
                    const groupNoteStartTime = groupTargetTime - MIN_NOTE_TRAVEL_TIME_PREVIEW;

                    chordGroup.forEach(noteData => {
                        // Ноты добавляются, hammer эффект создастся позже через updateHammerEffects
                        // Каждая группа нот имеет свой startTime, рассчитанный из targetTime
                        threeJSBand.addMovingNote(
                            noteData,
                            groupNoteStartTime, // Время создания = targetTime - MIN_NOTE_TRAVEL_TIME
                            groupTargetTime, // targetTime - момент достижения грифа
                            true, // activated = true для ДИНАМИЧНЫХ нот (они сразу летят!)
                            null
                        );
                    });

                    timeCursor += actualDuration;
                } else {
                    const pauseDuration = chordGroup[0].duration;
                    const isDotted = chordGroup[0].dotted || false;

                    const stepDuration = 4 / pauseDuration;
                    const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;
                    timeCursor += actualDuration;
                }
            });
        });

        // После добавления всех нот обновляем hammer эффекты
        if (threeJSBand) {
            threeJSBand.updateHammerEffects();
        }
    }

    // Обновление звезд блока
    updateBlockStars(blockId, starsCount) {
        if (!this.state.stars[blockId] || starsCount > this.state.stars[blockId]) {
            this.state.stars[blockId] = starsCount;
            updateStarsDisplay(blockId, starsCount);
            const isBlockCompleted = this.state.currentSpeedStep >= this.speedSteps.length - 1;
            // updateBestSpeedLevel = true, потому что updateBlockStars вызывается только когда уровень УСПЕШНО ПРОЙДЕН
            this.saveProgress({ completed: isBlockCompleted, updateBestSpeedLevel: true }).catch(err => {
                console.error('Ошибка при сохранении прогресса блока:', err);
            });
        }
    }

    // Откат прогресса блока на 1 шаг назад
    async rollbackBlockProgress(blockId) {
        const existingProgress = this.blockProgress[blockId] || {};
        const currentBestSpeedLevel = Number(existingProgress.best_speed_level || 0);
        const minBlockLevel = getBlockMinGameLevel(blockId) || 0;
        
        // Откатываем на 1 шаг назад, но не ниже min_game_level блока
        const newBestSpeedLevel = Math.max(minBlockLevel, currentBestSpeedLevel - 1);
        
        const userId = Number(this.userId || existingProgress.user_id || 1);
        const songId = Number(this.songId || existingProgress.song_id || 1);
        
        // Звезды показывают количество пройденных уровней: best_speed_level + 1
        // best_speed_level = 0 → 1 звезда, best_speed_level = 4 → 5 звезд
        const newStarsCount = newBestSpeedLevel + 1;
        
        const progressPayload = {
            user_id: userId,
            song_id: songId,
            block_id: blockId,
            stars: newStarsCount, // Сохраняем новое количество звезд
            accuracy: Number(existingProgress.accuracy || 0),
            best_speed_level: newBestSpeedLevel,
            attempts: Number(existingProgress.attempts || 0),
            completed: Number(existingProgress.completed || 0),
            play_time_seconds: 0 // Не изменяем время при откате
        };

        try {
            await saveUserProgress(progressPayload);
            this.blockProgress[blockId] = { ...existingProgress, ...progressPayload };
            
            // Обновляем локальное состояние звезд
            this.state.stars[blockId] = newStarsCount;
            
            // Перезагружаем прогресс, чтобы убедиться, что все синхронизировано
            await this.loadProgress();
            
            // Обновляем визуальное отображение звезд после загрузки прогресса
            // Используем актуальное значение из state.stars (которое синхронизировано с best_speed_level)
            const finalStarsCount = this.state.stars[blockId] || newStarsCount;
            updateStarsDisplay(blockId, finalStarsCount);
            
            return newBestSpeedLevel;
        } catch (error) {
            console.error('Не удалось откатить прогресс блока:', error);
            throw error;
        }
    }

    // Геттеры
    getGameMode() {
        return this.state.isPlaying;
    }

    getSequence() {
        return this.state.measures;
    }

    // Возвращает количество нот в последовательности (без пауз)
    getNotesCount() {
        return this.state.measures.filter(chordGroup => {
            return !(chordGroup && chordGroup[0] && chordGroup[0].pause === "true");
        }).length;
    }

    getCurrentGameTempo() {
        const speedFactor = this.speedSteps[this.state.currentSpeedStep];
        const tempo = getCurrentTempo();
        return speedFactor > 0 ? tempo * speedFactor : tempo;
    }

    getSpeedFactor() {
        return this.speedSteps[this.state.currentSpeedStep];
    }

    getGameStars(blockId) {
        return this.state.stars[blockId] || 0;
    }

    // Сброс прогресса
    async resetGameProgress() {
        let userId = Number(this.userId) || null;

        if (!userId) {
            try {
                const config = await getUserConfig();
                userId = Number(config?.user?.id || config?.userProgress?.[0]?.user_id);
            } catch (e) {
                console.error('Не удалось получить конфигурацию пользователя для сброса прогресса:', e);
            }
        }

        if (userId) {
            try {
                const response = await resetUserProgress(userId);
                if (!response?.success) {
                    console.warn('Сервер не подтвердил сброс прогресса пользователя');
                }
            } catch (error) {
                console.error('Не удалось сбросить прогресс пользователя на сервере:', error);
            }
        } else {
            console.warn('userId не определен, локально сбрасываем прогресс без обращения к серверу');
        }

        localStorage.removeItem(GAME_CONFIG.SAVE_KEY);
        this.blockProgress = {};
        this.state.reset();

        // Сбрасываем кэш прогресса и состояния игры,
        // чтобы при следующей инициализации данные были перезагружены без перезагрузки страницы.
        try {
            resetCachedUserProgress();
        } catch (e) {
            console.warn('Не удалось сбросить кэш прогресса пользователя:', e);
        }
    }

    // Настройка обработчиков кликов на блоки
    setupBlockClickHandlers() {
        // Обрабатываем как .melody-block, так и .sequence-block
        const blockElements = document.querySelectorAll('.melody-block, .sequence-block');
        blockElements.forEach(block => {
            // Удаляем старые обработчики, если они есть, создавая новый элемент
            const newBlock = block.cloneNode(true);
            block.parentNode.replaceChild(newBlock, block);
            
            newBlock.addEventListener('click', async (e) => {
                // Блокируем клики на блоки, пока выполняется handlePlayAll
                if (window.app && window.app.isPlayingPlayAll) {
                    return;
                }
                
                // Проверяем, нет ли блокирующих overlay на экране
                const loadingOverlay = document.querySelector('.loading-overlay');
                const countdownOverlay = document.querySelector('.countdown-overlay');
                if (loadingOverlay || countdownOverlay) {
                    // Если есть overlay, принудительно удаляем их
                    if (loadingOverlay && loadingOverlay.parentNode) {
                        loadingOverlay.parentNode.removeChild(loadingOverlay);
                    }
                    if (countdownOverlay && countdownOverlay.parentNode) {
                        countdownOverlay.parentNode.removeChild(countdownOverlay);
                    }
                    // Останавливаем countdown, если он активен
                    try {
                        const { stopCountdown } = await import('./utils/countdown.js');
                        stopCountdown();
                    } catch (e) {
                        // Игнорируем ошибки импорта
                    }
                }
                
                const blockId = parseInt(newBlock.dataset.blockId);
                if (!blockId) {
                    return;
                }
                
                try {
                    
                    // Прерываем запуск музыки, если он выполняется
                    if (window.app && window.app.isStartingPlayMusic && window.app.startPlayMusicAbortController) {
                        window.app.startPlayMusicAbortController.abort();
                        window.app.isStartingPlayMusic = false;
                    }
                    
                    // Устанавливаем запрошенный блок ID ДО вызова handleStopPlay
                    // Это позволит прервать текущий запуск блока, если он еще выполняется
                    this.requestedBlockId = blockId;
                    
                    // Сначала вызываем handleStopPlay перед запуском игры
                    // Используем таймаут, чтобы не блокировать UI слишком долго
                    if (window.app && typeof window.app.handleStopPlay === 'function') {
                        const stopPromise = window.app.handleStopPlay();
                        const timeoutPromise = new Promise(resolve => setTimeout(resolve, 1000));
                        await Promise.race([stopPromise, timeoutPromise]);
                    }
                    
                    // Проверяем, не был ли запрошен другой блок во время остановки
                    if (this.requestedBlockId !== blockId) {
                        return; // Другой блок был запрошен, прерываем
                    }
                    
                    // Останавливаем последовательность
                    const { sequencePlayer } = await import('./sequencePlayer.js');
                    if (sequencePlayer) {
                        await sequencePlayer.stop();
                    }
                    
                    // Запускаем выбранный блок
                    await this.startGameFromBlock(blockId);
                } catch (error) {
                    console.error('Ошибка при обработке клика на блок:', error);
                } finally {
                    // Снимаем флаг обработки
                    newBlock.dataset.processing = 'false';
                }
            });
        });
    }

    // Добавляем метод для остановки игры
    getCurrentLevel() {
        return this.state.currentLevel;
    }

    stopGame() {
        this.state.isPlaying = false;
        this.state.isGaming = false;
        this.stopBackgroundTrack();
        stopSequence();

        // Обновляем UI чтобы скрыть пульс
        this.updateGameUI();

        // Выходим из полноэкранного режима при остановке игры
        exitFullscreen().catch(err => {
            console.warn('Не удалось выйти из полноэкранного режима:', err);
        });
    }
}

// Создаем и экспортируем экземпляр
const gameManager = new GameManager();
// Делаем gameManager доступным глобально для handleStopPlay
window.gameManager = gameManager;

// Экспортируем методы с привязкой контекста
/** Инициализирует игровой режим для выбранной песни/трека */
export const initGame = gameManager.initGame.bind(gameManager);
/** Загружает сохраненный прогресс пользователя */
export const loadProgress = gameManager.loadProgress.bind(gameManager);
/** Обработка попадания пользователя в текущую ноту */
export const handleDetectedNote = gameManager.handleDetectedNote.bind(gameManager);
/** Флаг активного игрового режима */
export const getGameMode = gameManager.getGameMode.bind(gameManager);
/** Возвращает текущую последовательность нот/мер для активного блока */
export const getSequence = gameManager.getSequence.bind(gameManager);
/** Текущий темп с учетом фактора скорости уровня */
export const getCurrentGameTempo = gameManager.getCurrentGameTempo.bind(gameManager);
/** Текущий фактор скорости уровня (0..1) */
export const getSpeedFactor = gameManager.getSpeedFactor.bind(gameManager);
/** Проверяет завершение уровня и подготавливает следующий */
export const checkLevelCompletion = gameManager.checkLevelCompletion.bind(gameManager);
/** Полностью сбрасывает прогресс игры */
export const resetGameProgress = gameManager.resetGameProgress.bind(gameManager);
/** Возвращает кол-во звезд для блока */
export const getGameStars = gameManager.getGameStars.bind(gameManager);
/** Навешивает обработчики кликов на блоки мелодии */
export const setupBlockClickHandlers = gameManager.setupBlockClickHandlers.bind(gameManager);
/** То же, что getGameMode (совместимость) */
export const getGamingMode = gameManager.getGamingMode.bind(gameManager);
/** Останавливает игровой режим и очищает состояние */
export const stopGame = gameManager.stopGame.bind(gameManager);
/** Получает прогресс всех блоков */
export const getBlockProgress = () => gameManager.blockProgress;
/** Запускает анимацию "удара сердца" на кардиографе */
export const triggerHeartbeatJump = gameManager.triggerHeartbeatJump.bind(gameManager);
/** Обновляет игровой интерфейс (включая кардиограф) */
export const updateGameUI = gameManager.updateGameUI.bind(gameManager);